import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { stocksAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useBreakpoints } from '../hooks/useWindowWidth';

// ─── HAMBURGER ICON ───────────────────────────────────────────────────────────
function HamburgerIcon({ open, color }) {
  const s = { display: 'block', width: '18px', height: '2px', background: color, borderRadius: '2px', transition: 'all 0.22s ease' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '18px' }}>
      <span style={{ ...s, transform: open ? 'translateY(6px) rotate(45deg)' : 'none' }} />
      <span style={{ ...s, opacity: open ? 0 : 1, transform: open ? 'scaleX(0)' : 'none' }} />
      <span style={{ ...s, transform: open ? 'translateY(-6px) rotate(-45deg)' : 'none' }} />
    </div>
  );
}

// ─── MOBILE DRAWER ────────────────────────────────────────────────────────────
function MobileDrawer({ open, onClose, navLinks, location, user, mode, toggleMode, colors, fonts, nerveRgb, logout, navigate }) {
  const C = colors;
  const F = fonts;
  const isDark = mode === 'dark';

  if (!open) return null;

  const handleNav = (path) => { navigate(path); onClose(); };
  const handleLogout = () => { logout(); navigate('/'); onClose(); };

  return (
    <div className="mob-drawer" onClick={onClose}>
      <div className="mob-drawer-backdrop" />
      <div className="mob-drawer-panel" onClick={e => e.stopPropagation()}
        style={{ background: C.surface, borderLeft: `1px solid ${C.border}`, paddingTop: 'env(safe-area-inset-top)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.125rem 1.25rem', borderBottom: `1px solid ${C.border}` }}>
          <Link to="/dashboard" onClick={onClose} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src="/wordmark.png" alt="MarketNerve" style={{ height: '30px', objectFit: 'contain' }} />
          </Link>
          <button onClick={onClose} aria-label="Close navigation" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textDim, padding: '4px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* User info */}
        {user && (
          <div style={{ padding: '1rem 1.25rem', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {user.picture
              ? <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" style={{ width: '38px', height: '38px', borderRadius: '50%', border: `2px solid ${C.action}`, flexShrink: 0 }} />
              : <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: C.action, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', flexShrink: 0 }}>{user.name?.[0]?.toUpperCase() || 'U'}</div>
            }
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
              <div style={{ fontSize: '0.7rem', color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'JetBrains Mono', monospace" }}>{user.email}</div>
            </div>
            <span style={{ marginLeft: 'auto', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.action, background: `rgba(${nerveRgb},0.12)`, padding: '0.2rem 0.5rem', borderRadius: '2rem', border: `1px solid rgba(${nerveRgb},0.28)`, flexShrink: 0 }}>
              {user.plan || 'free'}
            </span>
          </div>
        )}

        {/* Nav links */}
        <nav style={{ padding: '0.75rem 0' }}>
          {navLinks.map(({ path, label, pro, comingSoon, enrolled }) => {
            const isActive = location.pathname === path;
            return (
              <button key={path} onClick={() => handleNav(path)}
                style={{
                  width: '100%', textAlign: 'left', padding: '0.75rem 1.25rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: isActive ? `rgba(${nerveRgb},0.08)` : 'none',
                  border: 'none', cursor: 'pointer',
                  color: isActive ? C.action : C.text,
                  fontSize: '0.9rem', fontWeight: isActive ? 600 : 400,
                  fontFamily: F.display, transition: 'background 0.15s',
                  borderLeft: isActive ? `3px solid ${C.action}` : '3px solid transparent',
                }}>
                <span>{label}</span>
                {enrolled && (
                  <span style={{ fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.action, background: `rgba(${nerveRgb},0.15)`, padding: '0.1rem 0.35rem', borderRadius: '2rem', border: `1px solid rgba(${nerveRgb},0.35)` }}>Enrolled</span>
                )}
                {comingSoon && (
                  <span style={{ fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#f59e0b', background: 'rgba(245,158,11,0.12)', padding: '0.1rem 0.35rem', borderRadius: '2rem', border: '1px solid rgba(245,158,11,0.3)' }}>Soon</span>
                )}
                {pro && !comingSoon && (
                  <span style={{ fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.actionHi, background: `rgba(${nerveRgb},0.15)`, padding: '0.1rem 0.35rem', borderRadius: '2rem', border: `1px solid rgba(${nerveRgb},0.3)` }}>Pro</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Divider */}
        <div style={{ height: '1px', background: C.border, margin: '0 1.25rem' }} />

        {/* Actions */}
        <div style={{ padding: '0.75rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {/* Theme toggle — above sign out */}
          <button onClick={toggleMode} aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} style={{
            display: 'flex', alignItems: 'center', gap: '0.625rem', width: '100%',
            padding: '0.625rem 0.875rem', borderRadius: '0.625rem',
            background: `rgba(${nerveRgb},0.07)`, border: `1px solid ${C.border}`,
            cursor: 'pointer', color: C.text, fontSize: '0.875rem', fontFamily: F.display,
          }}>
            {mode === 'dark'
              ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>Switch to Light Mode</>
              : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>Switch to Dark Mode</>
            }
          </button>
          {user && (
            <button onClick={handleLogout} style={{
              width: '100%', padding: '0.75rem', borderRadius: '0.625rem',
              background: `rgba(${nerveRgb},0.05)`, border: `1px solid ${C.border}`,
              color: C.bear, fontSize: '0.875rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              fontFamily: F.display,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Sign out
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── NAVBAR ───────────────────────────────────────────────────────────────────
export default function Navbar() {
  const [search, setSearch]             = useState('');
  const [results, setResults]           = useState([]);
  const [activeIdx, setActiveIdx]       = useState(-1);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen]     = useState(false);
  const [searchOpen, setSearchOpen]     = useState(false);
  const menuRef     = useRef(null);
  const searchTimer = useRef(null);
  const navigate    = useNavigate();
  const location    = useLocation();

  const { user, logout }                              = useAuth();
  const { colors, fonts, nerveRgb, bgRgb, mode, toggleMode } = useTheme();
  const { isMobile, isMd } = useBreakpoints();

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); setSearchOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    setActiveIdx(-1);
    clearTimeout(searchTimer.current);
    if (val.length > 1) {
      searchTimer.current = setTimeout(async () => {
        try {
          const res = await stocksAPI.search(val);
          setResults((res.data.quotes || []).slice(0, 6));
        } catch {}
      }, 250);
    } else { setResults([]); }
  };

  const handleSearchKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = activeIdx >= 0 ? results[activeIdx] : results[0];
      if (target) selectStock(target.symbol);
    } else if (e.key === 'Escape') {
      setResults([]); setActiveIdx(-1);
    }
  };

  const selectStock = (symbol) => {
    setSearch(''); setResults([]); setActiveIdx(-1); setSearchOpen(false);
    navigate(`/stock/${symbol}`);
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const navLinks = [
    { path: '/dashboard',   label: 'Dashboard'   },
    { path: '/watchlist',   label: 'Watchlist'   },
    // Quant disabled 2026-09-05 — fetches from Yahoo Finance, see App.js
    // { path: '/quant',       label: 'Quant'        },
    { path: '/seasonality', label: 'Seasonality'  },
    { path: '/macro',       label: 'Macro'        },
    // FPI Tracker disabled 2026-09-05 — pending NSDL licensing review, see App.js
    // { path: '/fpi',         label: 'FPI Tracker'  },
    // Market Intelligence and Research Lab disabled 2026-09-05 — see App.js
    // { path: '/market-intelligence', label: 'Market Intelligence' },
    // { path: '/research-lab', label: 'Research Lab' },
    { path: '/my-research',  label: 'My Research', comingSoon: true },
    { path: '/playbook',    label: 'Monthly Playbook', comingSoon: true },
    { path: '/portfolio',   label: 'Portfolio Doctor', comingSoon: true },
    { path: '/strategy',    label: 'Screener',    comingSoon: true },
    { path: '/ai-insights', label: 'AI Insights',    comingSoon: true },
    { path: '/mentorship',  label: 'Mentorship', enrolled: !!user?.mentorship },
  ];

  const C = colors;
  const F = fonts;
  const navBg = `rgba(${bgRgb},0.92)`;
  const isDark = mode === 'dark';

  return (
    <>
      <nav style={{
        background: navBg,
        borderBottom: `1px solid ${C.border}`,
        backdropFilter: 'blur(16px) saturate(140%)',
        WebkitBackdropFilter: 'blur(16px) saturate(140%)',
        fontFamily: F.display,
        transition: 'background 0.35s, border-color 0.35s',
        // With viewport-fit=cover (see public/index.html) the page now
        // extends under notches/the Dynamic Island instead of leaving a
        // blank bar there — this pushes the nav's own content back below
        // it while its background still fills that space, so nothing
        // ends up rendered behind the sensor housing itself.
        paddingTop: 'env(safe-area-inset-top)',
      }} className="sticky top-0 z-50">
        {/* Matches App.js's <main> cap (1800px, not the old 1280px) so the
            nav's content edges line up with the page content below it
            instead of reading as a narrower strip floating above a wider
            page. */}
        <div style={{ maxWidth: '1800px', margin: '0 auto', padding: '0 1rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem 1rem', padding: '0.625rem 0' }}>

            {/* Logo */}
            <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
              <img src="/wordmark.png" alt="MarketNerve" style={{ height: '32px', objectFit: 'contain' }} />
            </Link>

            {/* Desktop nav links */}
            {isMd && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem', flexBasis: '100%', order: 3 }}>
                {navLinks.map(({ path, label, pro, comingSoon, enrolled }) => {
                  const isActive = location.pathname === path;
                  return (
                    <Link key={path} to={path} style={{
                      color: isActive ? C.action : C.textMuted,
                      fontWeight: isActive ? 600 : 400,
                      display: 'flex', alignItems: 'center', gap: '0.3rem',
                      textDecoration: 'none', transition: 'color 0.15s', fontSize: '0.82rem', whiteSpace: 'nowrap', flexShrink: 0,
                    }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = C.text; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = C.textMuted; }}>
                      {label}
                      {enrolled && (
                        <span style={{ fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.action, background: `rgba(${nerveRgb},0.15)`, padding: '0.1rem 0.3rem', borderRadius: '2rem', border: `1px solid rgba(${nerveRgb},0.35)`, lineHeight: 1.4 }}>
                          ✓
                        </span>
                      )}
                      {comingSoon && (
                        <span style={{ fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#f59e0b', background: 'rgba(245,158,11,0.12)', padding: '0.1rem 0.3rem', borderRadius: '2rem', border: '1px solid rgba(245,158,11,0.3)', lineHeight: 1.4 }}>
                          Soon
                        </span>
                      )}
                      {pro && !comingSoon && (
                        <span style={{ fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.actionHi, background: `rgba(${nerveRgb},0.15)`, padding: '0.1rem 0.3rem', borderRadius: '2rem', border: `1px solid rgba(${nerveRgb},0.3)`, lineHeight: 1.4 }}>
                          Pro
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Right side */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexShrink: 0, order: 2 }}>


              {/* Mobile search toggle */}
              {isMobile && (
                <button onClick={() => setSearchOpen(v => !v)} aria-label={searchOpen ? 'Close search' : 'Open search'} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '6px 8px', cursor: 'pointer', color: C.textDim, display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHi; e.currentTarget.style.color = C.action; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textDim; }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </button>
              )}

              {/* Desktop search */}
              {isMd && (
                <div style={{ position: 'relative' }}>
                  <input value={search} onChange={handleSearch} onKeyDown={handleSearchKeyDown}
                    placeholder="Search stocks…"
                    style={{
                      background: C.inputBg, border: `1px solid ${C.border}`, color: C.text,
                      fontFamily: "'JetBrains Mono', monospace", fontSize: '0.78rem',
                      padding: '6px 10px', borderRadius: '8px', outline: 'none', width: '180px',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={e => e.target.style.borderColor = C.borderHi}
                    onBlur={e  => e.target.style.borderColor = C.border}
                  />
                  {results.length > 0 && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, width: '280px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', boxShadow: '0 16px 40px rgba(0,0,0,0.4)', zIndex: 60, overflow: 'hidden' }}>
                      {results.map((r, i) => (
                        <button key={r.symbol} onClick={() => selectStock(r.symbol)}
                          style={{ width: '100%', textAlign: 'left', padding: '0.625rem 0.875rem', display: 'flex', justifyContent: 'space-between', background: i === activeIdx ? C.surfaceHi : 'none', border: 'none', cursor: 'pointer', color: C.text, transition: 'background 0.15s', fontSize: '0.8rem' }}
                          onMouseEnter={e => { e.currentTarget.style.background = C.surfaceHi; setActiveIdx(i); }}
                          onMouseLeave={e => e.currentTarget.style.background = i === activeIdx ? C.surfaceHi : 'none'}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.actionHi }}>{r.symbol}</span>
                          <span style={{ color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginLeft: '0.5rem' }}>{r.shortname}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Desktop user avatar + dropdown */}
              {isMd && user && (
                <div style={{ position: 'relative' }} ref={menuRef}>
                  <button onClick={() => setUserMenuOpen(v => !v)} aria-label="Open user menu" aria-expanded={userMenuOpen}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', borderRadius: '2rem' }}>
                    {user.picture
                      ? <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" style={{ width: '32px', height: '32px', borderRadius: '50%', border: `2px solid ${C.action}` }} />
                      : <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: C.action, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem', color: '#fff' }}>{user.name?.[0]?.toUpperCase() || 'U'}</div>
                    }
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke={C.textDim} strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>

                  {userMenuOpen && (
                    <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: '240px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '0.875rem', boxShadow: `0 20px 50px rgba(0,0,0,0.45)`, zIndex: 60, overflow: 'hidden', fontFamily: F.display }}>
                      {/* User info */}
                      <div style={{ padding: '1rem', borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem', color: C.text, marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
                        <div style={{ fontSize: '0.7rem', color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'JetBrains Mono', monospace" }}>{user.email}</div>
                        <div style={{ marginTop: '0.5rem' }}>
                          <span style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.action, background: `rgba(${nerveRgb},0.12)`, padding: '0.2rem 0.6rem', borderRadius: '2rem', border: `1px solid rgba(${nerveRgb},0.28)` }}>
                            {user.plan || 'free'} plan
                          </span>
                        </div>
                      </div>


                      {/* Mode toggle — above sign out */}
                      <div style={{ padding: '0.625rem 1rem', borderBottom: `1px solid ${C.border}` }}>
                        <button onClick={toggleMode} aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: `rgba(${nerveRgb},0.07)`, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: C.textDim, fontSize: '0.78rem', fontWeight: 500, fontFamily: fonts.display, transition: 'all 0.18s', width: '100%' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = C.action; e.currentTarget.style.color = C.action; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textDim; }}>
                          {mode === 'dark'
                            ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>Switch to Light Mode</>
                            : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>Switch to Dark Mode</>
                          }
                        </button>
                      </div>

                      {/* Sign out */}
                      <button onClick={handleLogout} style={{ width: '100%', padding: '0.75rem 1rem', textAlign: 'left', background: 'none', border: 'none', color: C.bear, fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = `rgba(${nerveRgb},0.06)`}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.bear} strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Mobile hamburger */}
              {isMobile && (
                <button onClick={() => setMobileOpen(v => !v)}
                  style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = C.borderHi}
                  onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                  aria-label="Menu">
                  <HamburgerIcon open={mobileOpen} color={C.textMuted} />
                </button>
              )}
            </div>
          </div>

          {/* Mobile inline search */}
          {isMobile && searchOpen && (
            <div style={{ padding: '0 0 0.75rem', position: 'relative' }}>
              <input value={search} onChange={handleSearch} onKeyDown={handleSearchKeyDown} autoFocus
                placeholder="Search stocks… (AAPL, TCS.NS)"
                style={{
                  width: '100%', background: C.inputBg, border: `1px solid ${C.borderHi}`,
                  color: C.text, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.85rem',
                  padding: '0.625rem 0.875rem', borderRadius: '8px', outline: 'none', boxSizing: 'border-box',
                }}
              />
              {results.length > 0 && (
                <div style={{ position: 'absolute', top: 'calc(100% - 0.75rem + 2px)', left: 0, right: 0, background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', boxShadow: '0 16px 40px rgba(0,0,0,0.4)', zIndex: 60, overflow: 'hidden' }}>
                  {results.map((r, i) => (
                    <button key={r.symbol} onClick={() => selectStock(r.symbol)}
                      style={{ width: '100%', textAlign: 'left', padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', background: i === activeIdx ? C.surfaceHi : 'none', border: 'none', cursor: 'pointer', color: C.text, fontSize: '0.85rem' }}
                      onMouseEnter={e => { e.currentTarget.style.background = C.surfaceHi; setActiveIdx(i); }}
                      onMouseLeave={e => e.currentTarget.style.background = i === activeIdx ? C.surfaceHi : 'none'}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.actionHi }}>{r.symbol}</span>
                      <span style={{ color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginLeft: '0.5rem' }}>{r.shortname}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Drawer */}
      <MobileDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        navLinks={navLinks}
        location={location}
        user={user}
        mode={mode}
        toggleMode={toggleMode}
        colors={colors}
        fonts={fonts}
        nerveRgb={nerveRgb}
        logout={logout}
        navigate={navigate}
      />
    </>
  );
}
